import { Button, Stack, Typography } from '@mui/material'
import { forwardRef, useEffect } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import {
  DeliveryAddressInput,
  Maybe,
  UserDeliveryDetailFragment,
} from '@/clients/utoniq-core/schema'
import {
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { PostalCode } from '@/core/entities/values/PostalCode'
import {
  validateCity,
  validateLine1,
  validateLine2,
  validatePrefecture,
} from '@/core/validators'

type LotteryDeliveryFormProps = {
  user?: Maybe<UserDeliveryDetailFragment>
  onSubmit: (value: LotteryDeliveryValue) => Promise<void>
}

export type LotteryDeliveryValue = DeliveryAddressInput & {}

const schema: UseFormProps['schema'] = o => ({
  postalCode: o
    .string()
    .custom(PostalCode.createValidator())
    .label(tLabel('lotteryDeliveryInfo.postalCode')),
  prefecture: validatePrefecture(o, 'lotteryDeliveryInfo.prefecture'),
  city: validateCity(o, 'lotteryDeliveryInfo.city'),
  line1: validateLine1(o, 'lotteryDeliveryInfo.line1'),
  line2: validateLine2(o, 'lotteryDeliveryInfo.line2'),
})

export const LotteryDeliveryForm: Organism<LotteryDeliveryFormProps> = ({
  user,
  onSubmit,
}) => {
  const { t } = useTranslation('lotteryDeliveryInfo')
  const { showError } = useMessage()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LotteryDeliveryValue>({ schema, defaultValues })

  useEffect(() => {
    if (user) {
      reset({
        postalCode: user?.address?.postalCode ?? '',
        prefecture: user?.address?.prefecture ?? '',
        city: user?.address?.city ?? '',
        line1: user?.address?.line1 ?? '',
        line2: user?.address?.line2 ?? '',
      })
    }
  }, [user])

  const handleOnSubmit = handleSubmit(async values => {
    const normalizedValues = {
      ...values,
      postalCode: PostalCode.normalize(values.postalCode),
    }

    await onSubmit(normalizedValues).catch(showError)
  })

  return (
    <OrganismFrame sx={{ py: 4 }}>
      <Typography variant="subtitle1" sx={{ pb: 4 }}>
        {t('preSaleDeliveryNote', 'お客様の情報をご入力ください。')}
      </Typography>
      <TextInputCell
        type="tel"
        textFieldProps={{
          InputProps: {
            inputProps: {
              pattern: '\\d{3}-?\\d{4}|\\d{7}',
              inputMode: 'tel',
              sx: { px: 4, py: 3 },
            },
          },
        }}
        error={errors?.postalCode}
        {...register('postalCode')}
      />
      <TextInputCell
        type="text"
        error={errors?.prefecture}
        {...register('prefecture')}
      />
      <TextInputCell type="text" error={errors?.city} {...register('city')} />
      <TextInputCell type="text" error={errors?.line1} {...register('line1')} />
      <TextInputCell type="text" error={errors?.line2} {...register('line2')} />
      <Stack flex={1} alignItems="center" sx={{ pb: 5 }}>
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleOnSubmit}
        >
          {t('submit')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('lotteryDeliveryInfo')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const defaultValues: LotteryDeliveryValue = {
  postalCode: '',
  city: '',
  line1: '',
  line2: '',
  prefecture: '',
}
